import { forceLogout } from './force-logout';

// Operator-ui idle threshold per docs/ui/operator-ui-tds.md §7: 30 minutes.
// Operators work multi-hour shifts; aggressive idle disrupts work, unlike
// admin-ui (15 min) which has cross-tenant authority.

const DEFAULT_IDLE_MS = 30 * 60 * 1000;

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll'] as const;

interface IdleMonitorOpts {
  idleMs?: number;
}

/**
 * Starts a window-event-driven idle monitor. Returns a teardown function.
 *
 * Idle is measured by the absence of mouse / keyboard / touch / scroll events.
 * Network activity is intentionally not a signal — auto-polled queries would
 * falsely renew the session. On idle expiry, calls
 * `forceLogout({ reason: 'idle-timeout' })`.
 */
export function startIdleMonitor({
  idleMs = DEFAULT_IDLE_MS,
}: IdleMonitorOpts = {}): () => void {
  let timer: number | null = null;

  const reset = (): void => {
    if (timer !== null) window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      void forceLogout({ reason: 'idle-timeout' });
    }, idleMs);
  };

  for (const ev of ACTIVITY_EVENTS) {
    window.addEventListener(ev, reset, { passive: true });
  }
  reset();

  return () => {
    if (timer !== null) window.clearTimeout(timer);
    for (const ev of ACTIVITY_EVENTS) {
      window.removeEventListener(ev, reset);
    }
  };
}
